import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { DeviceHashes } from "@/hooks/useDeviceHashes";

interface CoachResult {
  rewriteSuggestions: string[];
  needsMoreDetail: boolean;
  hint: string;
  quotaExceeded?: boolean;
  quotaUsed?: number;
  quotaLimit?: number;
  coachingDisabled?: boolean;
}

const VAGUE_VERBS = ["lese", "rydde", "trene", "øve", "lekser", "dusje", "vaske", "spise", "sove", "gjøre", "jobbe", "lage"];

export function isTooVague(text: string): boolean {
  const trimmed = text.trim().toLowerCase();
  if (trimmed.length === 0) return false;
  const words = trimmed.split(/\s+/);
  if (words.length < 2) return true;
  const hasNumber = /\d/.test(trimmed);
  if (!hasNumber && VAGUE_VERBS.some((v) => trimmed.includes(v))) return true;
  return false;
}

export function useTaskCoach(deviceHashes?: DeviceHashes | null) {
  const [coach, setCoach] = useState<CoachResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [quotaUsed, setQuotaUsed] = useState<number | undefined>();
  const [quotaLimit, setQuotaLimit] = useState<number | undefined>();
  const [coachingDisabled, setCoachingDisabled] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTitleRef = useRef("");

  function check(title: string) {
    const trimmed = title.trim();

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (trimmed.length < 2 || !isTooVague(trimmed)) {
      setCoach(null);
      lastTitleRef.current = "";
      return;
    }

    // Don't call API if quota is already exceeded or coaching is disabled
    if (quotaExceeded || coachingDisabled) return;

    if (trimmed === lastTitleRef.current) return;

    debounceRef.current = setTimeout(async () => {
      lastTitleRef.current = trimmed;
      setIsLoading(true);
      try {
        const body: Record<string, string> = { title: trimmed };
        if (deviceHashes) {
          body.deviceIdHash = deviceHashes.deviceIdHash;
          body.sessionTokenHash = deviceHashes.sessionTokenHash;
        }

        const { data, error } = await supabase.functions.invoke("coach-task", {
          body,
        });
        if (error) throw error;
        const result = data as CoachResult;

        if (result.coachingDisabled) {
          setCoachingDisabled(true);
          setCoach(null);
        } else if (result.quotaExceeded) {
          setQuotaExceeded(true);
          setQuotaUsed(result.quotaUsed);
          setQuotaLimit(result.quotaLimit);
          setCoach(null);
        } else {
          setCoach(result);
          if (result.quotaUsed !== undefined) setQuotaUsed(result.quotaUsed);
          if (result.quotaLimit !== undefined) setQuotaLimit(result.quotaLimit);
        }
      } catch (err) {
        console.warn("Task coach failed:", err);
        setCoach(null);
      } finally {
        setIsLoading(false);
      }
    }, 800);
  }

  function reset() {
    setCoach(null);
    lastTitleRef.current = "";
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return { coach, isLoading, check, reset, quotaExceeded, quotaUsed, quotaLimit, coachingDisabled };
}
